import type { InvalidationMessage, RealtimeChannel, RealtimeMessage } from './messages';

// Browser-side WebSocket client. Auto-reconnects with exponential backoff,
// sends periodic pings, and dispatches invalidations to subscribers.
//
// Usage in apps/tender/web:
//   const rt = createRealtimeClient(`wss://${tenant}.subbietrac.app/api/_ws`);
//   rt.on('tenders', () => tendersCollection.utils.refetch());

export interface RealtimeClient {
  on(channel: RealtimeChannel | '*', handler: (msg: InvalidationMessage) => void): () => void;
  close(): void;
  readyState(): number;
}

export interface RealtimeClientOptions {
  /** Initial reconnect delay in ms. Backs off exponentially up to maxBackoffMs. */
  initialBackoffMs?: number;
  maxBackoffMs?: number;
  /** Ping interval to keep the connection warm and detect dead links. */
  pingIntervalMs?: number;
  /** Called when the connection state changes. */
  onStateChange?: (state: 'connecting' | 'open' | 'closed' | 'reconnecting') => void;
  /** Construct a custom WebSocket (for tests). */
  createSocket?: (url: string) => WebSocket;
}

export function createRealtimeClient(
  url: string,
  opts: RealtimeClientOptions = {},
): RealtimeClient {
  const initial = opts.initialBackoffMs ?? 500;
  const max = opts.maxBackoffMs ?? 30_000;
  const pingMs = opts.pingIntervalMs ?? 25_000;
  const make = opts.createSocket ?? ((u) => new WebSocket(u));

  const handlers = new Map<string, Set<(msg: InvalidationMessage) => void>>();
  let socket: WebSocket | null = null;
  let backoff = initial;
  let closed = false;
  let pingTimer: ReturnType<typeof setInterval> | null = null;

  const setState = (s: 'connecting' | 'open' | 'closed' | 'reconnecting') => {
    opts.onStateChange?.(s);
  };

  const connect = () => {
    if (closed) return;
    setState('connecting');
    socket = make(url);
    socket.addEventListener('open', () => {
      backoff = initial;
      setState('open');
      pingTimer = setInterval(() => {
        if (socket?.readyState === WebSocket.OPEN) {
          socket.send(JSON.stringify({ type: 'ping', ts: Date.now() }));
        }
      }, pingMs);
    });
    socket.addEventListener('message', (evt) => {
      const data = (evt as MessageEvent).data;
      if (typeof data !== 'string') return;
      let msg: RealtimeMessage;
      try {
        msg = JSON.parse(data) as RealtimeMessage;
      } catch {
        return;
      }
      if (msg.type === 'invalidate') {
        dispatch(msg);
      }
    });
    socket.addEventListener('close', () => {
      if (pingTimer) clearInterval(pingTimer);
      pingTimer = null;
      if (closed) {
        setState('closed');
        return;
      }
      setState('reconnecting');
      setTimeout(connect, backoff);
      backoff = Math.min(max, backoff * 2);
    });
    socket.addEventListener('error', () => {
      // close handler will fire next
    });
  };

  const dispatch = (msg: InvalidationMessage) => {
    handlers.get(msg.channel)?.forEach((h) => h(msg));
    handlers.get('*')?.forEach((h) => h(msg));
  };

  connect();

  return {
    on(channel, handler) {
      let set = handlers.get(channel);
      if (!set) {
        set = new Set();
        handlers.set(channel, set);
      }
      set.add(handler);
      return () => set?.delete(handler);
    },
    close() {
      closed = true;
      if (pingTimer) clearInterval(pingTimer);
      socket?.close();
    },
    readyState() {
      return socket?.readyState ?? WebSocket.CLOSED;
    },
  };
}
